import { TrackerData } from './types/TrackerData'
import * as constants from '../constants'
import * as trackerInternal from './tracker-internal/trackerInternal'
// eslint-disable-next-line import/no-restricted-paths
import { GraphicsTrackerData } from '../graphics/components/TemplateDetails/utils'
// eslint-disable-next-line import/no-restricted-paths
import { createExtensibleContext } from '../clipro/common/utils/extensibleContext'
import { ValueOf } from './utils/typeUtils'
import { EventProperties } from './tracker-internal/types/eventProperties'
// import config from '../config'

export function identify(id) {
  if (window.mixpanel) {
    window.mixpanel.identify(id)
  }
}

export function setUserInfo(userInfo) {
  if (window.mixpanel) {
    window.mixpanel.register(userInfo)
  }
}

export function track({
  sendImmediately = false, // When it's `true`, we send the event to the backaned immediately (without using debounce)
  eventName,
  context,
  subContext,
  product,
  ...additionalEventProperties
}: {
  eventName: string
  context: string
  subContext: string
  product: ValueOf<typeof constants.TRACKER_PRODUCT>
} & any) {
  if (!window.isTrackerEnabled) {
    return
  }

  const eventProperties: EventProperties = {
    context,
    subContext,
    product,
    ...additionalEventProperties,
  }

  if (window.mixpanel) {
    window.mixpanel.track(eventName, eventProperties)

    // DEBUG ONLY - uncomment and add `config.DEBUG = true` in `config.js` to see the event in the console
    // if ((config as any).DEBUG) {
    //   DEBUG_ONLY_consoleLogEvent(eventName, eventProperties)
    // }
  }

  if (!sendImmediately) {
    trackerInternal.addEvent({
      eventName,
      eventProperties,
    })
  } else {
    trackerInternal.sendEvent({
      eventName,
      eventProperties,
    })
  }
}

// function DEBUG_ONLY_consoleLogEvent(eventName, eventProperties) {
//   console.log(
//     `eventName: ${eventName}
//  `,
//     Object.entries(eventProperties).map(([key, value]) => `${key}: ${value}`).join(`
//   `),
//   )
// }

export function trackClipro(eventData: TrackerData) {
  track({
    ...eventData,
    product: constants.TRACKER_PRODUCT.CLIPRO,
  })
}

export function trackSettings(eventData: TrackerData) {
  track({
    ...eventData,
    product: constants.TRACKER_PRODUCT.SETTINGS,
  })
}

export function trackPublishHub(eventData: TrackerData) {
  track({
    ...eventData,
    product: constants.TRACKER_PRODUCT.PUBLISH_HUB,
  })
}

export function trackGraphisInStudio(eventData: TrackerData) {
  track({
    ...eventData,
    product: constants.TRACKER_PRODUCT.GRAPHICS_IN_STUDIO,
  })
}

export function trackAvgen({
  eventName,
  context,
  subContext = undefined,
  ...additionalEventProperties
}: TrackerData) {
  track({
    eventName,
    context,
    subContext,
    product: constants.TRACKER_PRODUCT.AVGEN,
    ...additionalEventProperties,
  })
}

export function trackCreate({
  eventName,
  subContext,
  sessionId,
  ...additionalEventProperties
}: TrackerData) {
  track({
    eventName,
    subContext,
    sessionId,
    context: constants.TRACKER_PRODUCT.CREATE,
    product: constants.TRACKER_PRODUCT.CREATE,
    ...additionalEventProperties,
  })
}

export function trackGraphics({
  eventName,
  context,
  subContext,
  ...additionalEventProperties
}: GraphicsTrackerData) {
  track({
    eventName,
    context,
    subContext,
    product: constants.TRACKER_PRODUCT.GRAPHICS,
    ...additionalEventProperties,
  })
}

export function trackLibrary(eventData: TrackerData) {
  track({
    ...eventData,
    product: constants.TRACKER_PRODUCT.LIBRARY,
  })
}

export function trackMobileLibrary(eventData: TrackerData) {
  track({
    ...eventData,
    product: constants.TRACKER_PRODUCT.LIBRARY_MOBILE,
  })
}

export function trackBlazeCms(eventData: TrackerData) {
  track({
    ...eventData,
    product: constants.TRACKER_PRODUCT.CMS,
  })
}

export function reset() {
  if (window.mixpanel) {
    window.mixpanel.reset()
  }
}

const [TrackerContextProvider, useTrackerContext] = createExtensibleContext<TrackerData>({})

export { TrackerContextProvider, useTrackerContext }

export const useTracker = () => {
  const trackerData = useTrackerContext()

  function track_(params: TrackerData) {
    const merged = { ...trackerData, ...params }

    track(merged)
  }

  return { track: track_, trackerData }
}
